/* eslint-disable react/no-unstable-nested-components */
import React, { useState, useCallback } from 'react'
import { copyToClipboard } from '@fs/zion-frontend-friends'
import {
  Bleed,
  Paragraph,
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  FlowGrid,
  InlineButton,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Separator,
  colors,
  useOverlay,
  useStatusOverlay,
  Skeleton,
  Row,
  ActionChip,
  TextField,
  H5,
} from '@fs/zion-ui'
import { ArrowCaret, NoticeWarning, ContentCopy } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useMutation } from '@apollo/client/react'
import { baseUrl } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import { useSlsStateContext } from '@fs/zion-sls'
import transl from './imageQualityAudit.transl'
import { useImageAuditContext } from '../../hooks/useImageAuditContext'
import mutations from '../imageBrowse/mutations'
import formatGroupName from '../../utils/formatGroupName'
import useReturnToAudit from '../../hooks/useReturnToAudit'
import AuditListItem from './components/AuditListItem'

const headerContainer = css`
  background-color: ${colors.gray02};
  padding: 1em;
`

const removeTextTransform = css`
  button {
    text-transform: none;
  }

  a {
    text-transform: none;
  }
`

const redText = css`
  color: ${colors.danger60};

  & * {
    color: ${colors.danger60};
    font-weight: bold;
  }
`

const padListStyles = css`
  padding: 0 1em;
`

export default function ImageQualityAuditInfoSheet() {
  const { audit, group, loading, qualityTags, qualityComments, originalAuditCount } = useImageAuditContext()
  const dialogOverlay = useOverlay()
  const [suggestedChange, setSuggestedChange] = useState('higher')
  const [additionalNotes, setAdditionalNotes] = useState('')
  const showStatusOverlay = useStatusOverlay()

  const zionSlsState = useSlsStateContext()

  const [doSuggestNewAuditPercentage] = useMutation(mutations.suggestNewAuditPercentage())

  const { returnToAudit } = useReturnToAudit()
  const projectId = group?.internalData?.phoenixAcquisitionIds

  async function submitNewAuditPercent() {
    const response = await doSuggestNewAuditPercentage()
    if (response.data.suggestNewImageAudit.success) {
      showStatusOverlay({
        message: 'Audit suggestion submitted successfully!',
        type: 'success',
      })
      dialogOverlay.close()
    } else
      showStatusOverlay({
        message: 'Audit suggestion submission failed',
        type: 'error',
      })
  }

  const copyText = useCallback(() => {
    copyToClipboard(group?.naturalGroupId, 'text/plain', () => {
      showStatusOverlay(
        {
          type: 'success',
          message: transl.copyImageNumberClipboardSuccess(),
          dismissable: true,
        },
        () => {
          showStatusOverlay({
            type: 'error',
            message: transl.copyImageNumberClipboardError(),
            dismissable: true,
          })
        }
      )
    })
  }, [group?.naturalGroupId, showStatusOverlay])

  function openToQualityStandards() {
    const language = i18n?.language || 'en'

    window.open(
      `https://www.familysearch.org/${language}/fieldops/article/image-quality-and-folder-metadata-image-quality-issue-examples-and-details`,
      '_blank'
    )
  }

  const hasQualityFlags = qualityComments || qualityTags.length > 0

  return loading ? (
    <>
      <Skeleton.Button />
      <Skeleton.H1 />
      {/* hide this until we have the functionality to suggest new audit percentage */}
      {/* <Skeleton.Body1 /> */}
      <Skeleton.Paragraph size="md" />
      <br />
      <Skeleton.ListItem hasIcon />
      <Skeleton.ListItem hasIcon />
      <Skeleton.ListItem hasIcon />
      <Skeleton.ListItem hasIcon />
      <Skeleton.ListItem hasIcon />
    </>
  ) : (
    <>
      <FlowGrid gutters="pico">
        <Bleed top sides css={headerContainer}>
          <FlowGrid gutters="pico">
            <Button
              Icon={ArrowCaret}
              iconDirection="backward"
              onClick={() => returnToAudit(projectId)}
              data-testid="back-to-audit-list"
            >
              {transl.backToAuditList()}
            </Button>
            <H5>{transl.imageQualityAudit()}</H5>
            <div css={removeTextTransform}>
              {/* add button back when we have functionality to suggest new audit percentage */}
              {/* <InlineButton onClick={dialogOverlay.open} data-testid="suggest-new-audit-button">
                {transl.suggestNewAuditPercentage()}
              </InlineButton> */}
              <Separator size="nano" />
              <InlineButton data-testid="go-to-image-quality-standards-button" onClick={openToQualityStandards}>
                {transl.imageQualityStandards()}
              </InlineButton>
              <List>
                <ListItem
                  overline={transl.imageGroupNumber()}
                  primaryText={
                    <InlineButton
                      title={transl.searchImageLinkTooltip()}
                      data-testid="search-image-group-number-button"
                      to={`${baseUrl}/ark:/61903/${zionSlsState?.currentImageArk}?view=explore&groupId=${group?.id}&grid=on`}
                      target="_blank"
                    >
                      {formatGroupName(group?.naturalGroupId)}
                    </InlineButton>
                  }
                  endElement={
                    <ListItem.IconButton
                      title={transl.copyImageNumberClipboardTooltip()}
                      data-testid="copy-image-group-number-button"
                      Icon={ContentCopy}
                      onClick={copyText}
                    />
                  }
                />
              </List>
            </div>
          </FlowGrid>
        </Bleed>
        {hasQualityFlags && (
          <>
            <div css={redText}>
              <Separator size="nano" />
              <Row alignY="middle" gutters="xxs">
                <NoticeWarning />
                <Paragraph size="sm">{transl.knownIssueHeader()}</Paragraph>
              </Row>
            </div>
            {qualityComments && (
              <>
                <Separator size="nano" />
                <Paragraph size="sm">{qualityComments}</Paragraph>
              </>
            )}
            <Separator size="xs" />
            {!!qualityTags.length && (
              <>
                <Row alignX="start" gutters="nano" wrap>
                  {qualityTags.map((qt, index) => (
                    <ActionChip label={qt} key={qt || index} />
                  ))}
                </Row>
                <Separator size="nano" />
              </>
            )}
          </>
        )}
        <Paragraph size="sm">{transl.reviewTheseImages(originalAuditCount)}</Paragraph>
        <div css={padListStyles}>
          <List>
            {audit?.map((i, index) => (
              <AuditListItem item={i} key={i.imageNumber || index} />
            ))}
          </List>
        </div>
      </FlowGrid>
      <DialogOverlay title={transl.suggestAChange()} {...dialogOverlay}>
        <RadioGroup>
          <Radio
            checked={suggestedChange === 'higher'}
            value="higher"
            name="higher-option"
            label={transl.increasePercentage()}
            onChange={(e) => setSuggestedChange(e.target.value)}
          />
          <Radio
            checked={suggestedChange === 'lower'}
            value="lower"
            name="lower-option"
            label={transl.decreasePercentage()}
            onChange={(e) => setSuggestedChange(e.target.value)}
          />
        </RadioGroup>
        <TextField
          placeholder={transl.additionalNotes()}
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
        />
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button emphasis="medium" onClick={submitNewAuditPercent} data-testid="suggest-new-audit-button">
              {transl.submit()}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}
